import React, { useMemo } from 'react';
import ScheduleSection from './ScheduleSection';

// Fallbacks used only when GET /tasks/config fails or hasn't returned yet.
// They mirror the canonical backend enum values so that the form is still
// usable offline / on a slow first paint, but every dropdown should
// normally render from the API response so a backend-side enum addition
// shows up without a frontend code change.
const FALLBACK_CATEGORIES = [
    { value: 'general', label: 'General' },
];
const FALLBACK_PRIORITIES = [
    { value: 'Low', label: 'Low' },
    { value: 'Medium', label: 'Medium' },
    { value: 'High', label: 'High' },
];
const FALLBACK_STATUSES = [
    { value: 'To Do', label: 'To Do' },
    { value: 'In Progress', label: 'In Progress' },
    { value: 'Blocked', label: 'Blocked' },
    { value: 'Completed', label: 'Completed' },
];

const inputClasses = "w-full rounded-xl py-4 px-6 bg-[var(--bg-card)] border border-[var(--border-visible)] text-[var(--text-main)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-bg)] theme-transition";

const labelClasses = "block text-sm font-semibold text-[var(--text-secondary)] mb-2";

const sectionTitleClasses = "text-xs font-bold tracking-widest text-[var(--text-muted)] uppercase mb-4 font-['Plus_Jakarta_Sans']";

const Section = ({ title, children }) => (
    <section>
        <h3 className={sectionTitleClasses}>{title}</h3>
        {/* p-4 below `md`: p-6 here plus the page's own gutters left the Frequency
            pills without room for their full labels on a 360px phone. */}
        <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border)] p-4 md:p-8 shadow-sm theme-transition">
            {children}
        </div>
    </section>
);

const TaskForm = ({
    formData,
    onChange,
    houses = [],
    rooms = [],
    items = [],
    categories = [],
    priorities = [],
    statuses = [],
    isEditMode,
}) => {
    const setField = (k, v) => onChange({ [k]: v });
    const categoryOptions = categories.length > 0 ? categories : FALLBACK_CATEGORIES;
    const priorityOptions = priorities.length > 0 ? priorities : FALLBACK_PRIORITIES;
    const statusOptions = statuses.length > 0 ? statuses : FALLBACK_STATUSES;

    // Rooms in the chosen house (or all rooms when no house is chosen).
    const filteredRooms = useMemo(() => {
        if (!formData.house_id) return rooms;
        return rooms.filter(r => String(r.house_id) === String(formData.house_id));
    }, [rooms, formData.house_id]);

    // Items in the current room; else items in the chosen house; else all.
    const filteredItems = useMemo(() => {
        if (formData.room_id) {
            return items.filter(it => String(it.room_id) === String(formData.room_id));
        }
        if (formData.house_id) {
            return items.filter(it => String(it.house_id) === String(formData.house_id));
        }
        return items;
    }, [items, formData.room_id, formData.house_id]);

    // Picking a house narrows rooms/items — drop selections that no longer
    // belong. Picking a room pulls its house along (the backend derives the
    // anchor the same way: room wins).
    const handleHouseChange = (value) => {
        const patch = { house_id: value };
        // Unresolvable selections (a failed rooms/items load leaves stale
        // ids) count as incompatible — keeping them would submit
        // conflicting anchors the backend rejects.
        if (value && formData.room_id) {
            const room = rooms.find(r => String(r.id) === String(formData.room_id));
            if (!room || String(room.house_id) !== String(value)) {
                patch.room_id = '';
                patch.item_id = '';
            }
        }
        if (value && patch.item_id === undefined && formData.item_id) {
            const item = items.find(it => String(it.id) === String(formData.item_id));
            if (!item || String(item.house_id) !== String(value)) {
                patch.item_id = '';
            }
        }
        onChange(patch);
    };

    const handleRoomChange = (value) => {
        const patch = { room_id: value };
        if (value) {
            const room = rooms.find(r => String(r.id) === String(value));
            if (room && room.house_id) patch.house_id = String(room.house_id);
            // Clear an item that doesn't belong to the new room — or that
            // can't be resolved at all (stale id after a failed load).
            if (formData.item_id) {
                const item = items.find(it => String(it.id) === String(formData.item_id));
                if (!item || String(item.room_id) !== String(value)) {
                    patch.item_id = '';
                }
            }
        }
        onChange(patch);
    };

    return (
        <div className="space-y-10">
            <Section title="Basics">
                <div className="space-y-6">
                    <div>
                        <label htmlFor="task-title" className={labelClasses}>Task Title <span className="text-[var(--error)]">*</span></label>
                        <input
                            id="task-title"
                            type="text"
                            placeholder="e.g. Replace HVAC filter"
                            value={formData.title}
                            onChange={e => setField('title', e.target.value)}
                            className={inputClasses}
                            maxLength={128}
                        />
                    </div>
                    <div>
                        <label htmlFor="task-description" className={labelClasses}>Description</label>
                        <textarea
                            id="task-description"
                            rows={4}
                            placeholder="Add notes about this task..."
                            value={formData.description}
                            onChange={e => setField('description', e.target.value)}
                            className={`${inputClasses} resize-y`}
                            maxLength={500}
                        />
                    </div>
                </div>
            </Section>

            <Section title="Classification">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                    <div>
                        <label htmlFor="task-category" className={labelClasses}>Category</label>
                        <select
                            id="task-category"
                            value={formData.category}
                            onChange={e => setField('category', e.target.value)}
                            className={inputClasses}
                        >
                            {categoryOptions.map(c => (
                                <option key={c.value} value={c.value}>{c.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="task-priority" className={labelClasses}>Priority</label>
                        <select
                            id="task-priority"
                            value={formData.priority}
                            onChange={e => setField('priority', e.target.value)}
                            className={inputClasses}
                        >
                            {priorityOptions.map(p => (
                                <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </Section>

            <Section title="Schedule">
                <ScheduleSection formData={formData} onChange={onChange} />
            </Section>

            <Section title="Location & Asset">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                    <div className="md:col-span-2">
                        <label htmlFor="task-house" className={labelClasses}>House</label>
                        <select
                            id="task-house"
                            value={formData.house_id || ''}
                            onChange={e => handleHouseChange(e.target.value)}
                            className={inputClasses}
                        >
                            <option value="">None</option>
                            {houses.map(h => (
                                <option key={h.id} value={h.id}>{h.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="task-room" className={labelClasses}>Room</label>
                        <select
                            id="task-room"
                            value={formData.room_id || ''}
                            onChange={e => handleRoomChange(e.target.value)}
                            className={inputClasses}
                        >
                            <option value="">Unassigned</option>
                            {filteredRooms.map(r => (
                                <option key={r.id} value={r.id}>{r.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="task-item" className={labelClasses}>Item / Asset</label>
                        <select
                            id="task-item"
                            value={formData.item_id || ''}
                            onChange={e => setField('item_id', e.target.value)}
                            className={inputClasses}
                        >
                            <option value="">None</option>
                            {filteredItems.map(it => (
                                <option key={it.id} value={it.id}>{it.name}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </Section>

            <Section title="Effort">
                <div>
                    <label htmlFor="task-duration" className={labelClasses}>Estimated duration (minutes)</label>
                    <input
                        id="task-duration"
                        type="number"
                        min="0"
                        placeholder="e.g. 30"
                        value={formData.estimated_duration}
                        onChange={e => setField('estimated_duration', e.target.value)}
                        className={inputClasses}
                    />
                </div>
            </Section>

            {isEditMode && (
                <Section title="Status">
                    <div>
                        <label htmlFor="task-status" className={labelClasses}>Status</label>
                        <select
                            id="task-status"
                            value={formData.status}
                            onChange={e => setField('status', e.target.value)}
                            className={inputClasses}
                        >
                            {statusOptions.map(s => (
                                <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                        </select>
                    </div>
                </Section>
            )}
        </div>
    );
};

export default TaskForm;
